import { formatUnit } from './units';
import { THOUSANDS_GROUPING } from './numberFormatting';

export interface CargoQuantityLike {
  quantity?: number | string | null;
  unit?: string | null;
  commercialQuantity?: number | string | null;
  commercialUnit?: string | null;
}

export type CargoQuantityTotals = Record<string, number>;

const UNIT_DISPLAY_ORDER = ['pcs', 'm2', 'm3', 'kg', 'mb'];

function normalizeCargoUnit(unit: unknown): string {
  return String(unit || 'pcs').trim().toLowerCase() || 'pcs';
}

function sortUnitEntries([leftUnit]: [string, number], [rightUnit]: [string, number]): number {
  const leftIndex = UNIT_DISPLAY_ORDER.indexOf(leftUnit);
  const rightIndex = UNIT_DISPLAY_ORDER.indexOf(rightUnit);

  if (leftIndex !== -1 || rightIndex !== -1) {
    return (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex)
      - (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex);
  }

  return leftUnit.localeCompare(rightUnit);
}

export function aggregateCargoQuantities(items: CargoQuantityLike[]): CargoQuantityTotals {
  return items.reduce<CargoQuantityTotals>((totals, item) => {
    const quantity = Number(item.quantity || 0);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      return totals;
    }

    const unit = normalizeCargoUnit(item.unit);
    totals[unit] = (totals[unit] || 0) + quantity;
    return totals;
  }, {});
}

export function formatCargoQuantityValue(value: number, locale = 'pl-PL'): string {
  return Number(value.toFixed(3)).toLocaleString(locale, {
    maximumFractionDigits: 3,
    ...THOUSANDS_GROUPING,
  } as Intl.NumberFormatOptions);
}

export function formatCargoQuantityTotals(
  totals: CargoQuantityTotals,
  locale = 'pl-PL',
  separator = ' + ',
): string {
  const entries = Object.entries(totals)
    .filter(([, quantity]) => Number.isFinite(quantity) && quantity > 0)
    .sort(sortUnitEntries);

  if (entries.length === 0) {
    return '-';
  }

  return entries
    .map(([unit, quantity]) => `${formatCargoQuantityValue(quantity, locale)} ${formatUnit(unit, locale)}`)
    .join(separator);
}

export function formatCargoQuantitySummary(items: CargoQuantityLike[], locale = 'pl-PL'): string {
  return formatCargoQuantityTotals(aggregateCargoQuantities(items), locale);
}

export function formatCargoQuantityPair(item: CargoQuantityLike, locale = 'pl-PL'): string {
  const physical = `${formatCargoQuantityValue(Number(item.quantity || 0), locale)} ${formatUnit(item.unit, locale)}`;
  const commercialQuantity = Number(item.commercialQuantity);
  if (!item.commercialUnit || !Number.isFinite(commercialQuantity) || commercialQuantity <= 0) {
    return physical;
  }
  return `${physical} / ${formatCargoQuantityValue(commercialQuantity, locale)} ${formatUnit(item.commercialUnit, locale)}`;
}
